<template>
    <div class="dt-body">
        <template v-if="loading && !rows.length">
            <div v-if="loader === 'skeleton'" class="dt-skeleton-wrap">
                <div v-for="i in 8" :key="i" class="dt-skeleton-row">
                    <span v-if="selectable" class="dt-skeleton-cell dt-skeleton-cell--cb"></span>
                    <span v-for="col in columns" :key="col.key" class="dt-skeleton-cell" :style="skeletonStyle(col)">
                        <span class="dt-skeleton-bar"></span>
                    </span>
                </div>
            </div>
            <div v-else class="dt-spinner-wrap">
                <span class="dt-spinner"></span>
            </div>
        </template>
        <template v-else-if="!rows.length">
            <div class="dt-empty">
                <svg class="dt-empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>
                <span class="dt-empty-text">{{ $t('common.dataTable.noData') }}</span>
            </div>
        </template>
        <template v-else>
            <div v-if="loading" class="dt-loading-overlay">
                <span class="dt-spinner"></span>
            </div>
            <DataTableRow v-for="row in rows" :key="row[rowKey]"
                :row="row"
                :columns="columns"
                :row-key="rowKey"
                :selectable="selectable"
                :expandable="expandable"
                :has-actions="hasActions"
                :actions="actions"
                :action-style="actionStyle"
                :is-selected="selectedIds.has(row[rowKey])"
                :is-expanded="expandedIds.has(row[rowKey])"
                :editing-cell="editingCell"
                :editable="editable"
                :render-expand="renderExpand"
                :column-widths="columnWidths"
                :is-compact="isCompact"
                @select="$emit('select', $event)"
                @expand="$emit('expand', $event)"
                @action="(key, r) => $emit('action', key, r)"
                @row-click="$emit('row-click', $event)"
                @edit-start="(rk, ck) => $emit('edit-start', rk, ck)"
                @edit-commit="(rk, ck, v) => $emit('edit-commit', rk, ck, v)"
                @edit-cancel="$emit('edit-cancel')"/>
            <div v-if="loadingMore" class="dt-loading-more">
                <span class="dt-spinner dt-spinner--sm"></span>
            </div>
            <div v-if="pagination === 'scroll' && hasMore && !loadingMore" ref="sentinel" class="dt-sentinel"></div>
        </template>
    </div>
</template>

<script>
import DataTableRow from './DataTableRow.vue'

export default {
    name: 'DataTableBody',
    components: { DataTableRow },
    props: {
        rows: Array,
        columns: Array,
        rowKey: String,
        selectable: Boolean,
        expandable: Boolean,
        hasActions: Boolean,
        actions: Array,
        actionStyle: String,
        selectedIds: { type: Set, default: () => new Set() },
        expandedIds: { type: Set, default: () => new Set() },
        editingCell: Object,
        editable: Boolean,
        renderExpand: Function,
        loading: Boolean,
        loadingMore: Boolean,
        hasMore: Boolean,
        pagination: String,
        loader: String,
        columnWidths: Object,
        isCompact: Boolean,
    },
    emits: ['select', 'expand', 'action', 'row-click', 'edit-start', 'edit-commit', 'edit-cancel', 'load-more'],
    created() {
        this.scrollObserver = null
    },
    methods: {
        skeletonStyle(col) {
            if (col.width) return { width: col.width + 'px', flexGrow: 0, flexShrink: 0 }
            return { flex: col.flex ?? 1 }
        },
        _initObserver() {
            this._destroyObserver()
            if (this.pagination !== 'scroll') return
            this.$nextTick(() => {
                const el = this.$refs.sentinel
                if (!el) return
                this.scrollObserver = new IntersectionObserver((entries) => {
                    if (entries[0]?.isIntersecting && this.hasMore && !this.loadingMore) {
                        this.$emit('load-more')
                    }
                }, { rootMargin: '200px' })
                this.scrollObserver.observe(el)
            })
        },
        _destroyObserver() {
            if (this.scrollObserver) { this.scrollObserver.disconnect(); this.scrollObserver = null }
        },
    },
    watch: {
        hasMore() { this._initObserver() },
        rows() { this.$nextTick(() => this._initObserver()) },
    },
    mounted() {
        this._initObserver()
    },
    beforeUnmount() {
        this._destroyObserver()
    },
}
</script>
